import type { ProductListItem, ProductStatus, StockLine } from '@wms/domain';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../api';
import { fmt } from '../../format';
import { navigate, useRoute } from '../../router';
import { useSession } from '../../session';
import { STOCK_FILTERS, inFilter, linkedFilter, withStock, type ProductStock, type StockFilter } from '../../stock';
import { Empty, ErrorBanner, ProductThumb, Spinner, useLoad } from '../../ui';
import { Icon } from '../../ui/icons';
import { useSort } from '../../ui/sort';
import { EmptyCatalog, STATUS_LABELS, labelItems, usePicked } from '../catalog/Products';
import { ProductLabelDialog } from '../catalog/ProductLabel';
import { useSellerPick } from '../catalog/SellerPick';
import { ExpiryChip } from '../Stock';

const STATUSES: readonly { readonly key: ProductStatus | ''; readonly label: string }[] = [
  { key: 'active', label: 'Aktif ürünler' },
  { key: 'inactive', label: 'Pasif ürünler' },
  { key: '', label: 'Arşiv dahil tümü' },
];

/**
 * The list as it was when a product was opened from it: coming back (the back link or gesture) shows it so.
 * Its user's only: signing out keeps the page, and the next person must not get the last one's search.
 */
let openedFrom: { readonly userId: string; readonly filter: StockFilter; readonly status: ProductStatus | ''; readonly search: string } | null = null;

/**
 * A seller's "Ürünler ve stok": the catalog with each product's stock (kullanılabilir, rezerve,
 * kabulde, bloke, nearest expiry) and its filters; a row opens the product with its lots.
 * The catalog and the stock come from their own lists, both cut to the seller by RLS.
 */
export function SellerProducts() {
  const pick = useSellerPick();
  const userId = useSession().me.id;
  // links from the start screen (and old #/stok bookmarks) open on their filter, every status included as its tiles count
  const linked = linkedFilter(useRoute().query);
  // back from a product opened here: the same filter, status and search (a link's own filter wins)
  const [back] = useState(() => (!linked && openedFrom?.userId === userId ? openedFrom : null));
  useEffect(() => {
    openedFrom = null;
  }, []);
  const [filter, setFilter] = useState<StockFilter>(back?.filter ?? linked ?? 'all');
  const [status, setStatus] = useState<ProductStatus | ''>(back?.status ?? (linked ? '' : 'active'));
  const [search, setSearch] = useState(back?.search ?? '');
  const [term, setTerm] = useState(back?.search.trim() ?? '');
  // the same screen, another link (the menu entry after a start-screen tile): its filter or none, and no search
  const shown = useRef(linked);
  useEffect(() => {
    if (shown.current === linked) return;
    shown.current = linked;
    setFilter(linked ?? 'all');
    setStatus(linked ? '' : 'active');
    setSearch('');
    setTerm('');
  }, [linked]);
  useEffect(() => {
    const timer = setTimeout(() => setTerm(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);
  // every status at once: the status choice and the counts on the chips need no new request
  const list = useLoad(() => api<ProductListItem[]>('GET', `/api/products${term ? `?${new URLSearchParams({ q: term })}` : ''}`), [term]);
  const stock = useLoad(() => api<StockLine[]>('GET', '/api/stock'), [], 30_000);
  const all = useMemo(() => withStock(list.data ?? [], stock.data ?? []), [list.data, stock.data]);
  const listed = all.filter((p) => !status || p.status === status);
  const filtered = listed.filter((p) => inFilter(p, filter));
  const blocked = filtered.some((p) => p.blocked > 0);
  const sort = useSort<ProductStock>('seller-products', {
    product: { label: 'Ürün', value: (p) => p.name },
    available: { label: 'Kullanılabilir', value: (p) => p.available, desc: true },
    reserved: { label: 'Rezerve', value: (p) => p.reserved, desc: true },
    inbound: { label: 'Kabulde / yolda', value: (p) => p.inbound, desc: true },
    ...(blocked ? { blocked: { label: 'Bloke', value: (p) => p.blocked, desc: true } } : {}),
    expiry: { label: 'En yakın SKT', value: (p) => p.nextExpiry },
    listings: { label: 'Pazaryeri', value: (p) => p.listings, desc: true },
  });
  const rows = sort.apply(filtered);
  const picked = usePicked(rows);
  const [labeling, setLabeling] = useState<readonly ProductListItem[] | null>(null);
  const loaded = list.data && (stock.data || stock.error);
  // the counts on the chips only with the stock known (a failed stock list would count every product out of stock)
  const counted = Boolean(list.data && stock.data);
  const total = (of: (p: (typeof rows)[number]) => number) => fmt.n(rows.reduce((n, p) => n + of(p), 0));

  return (
    <>
      <div className="row ps-bar">
        <input className="input ps-search" placeholder="SKU, ürün adı veya barkod" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Ürün ara" />
        {sort.picker}
        <span className="spacer" style={{ flex: 1 }} />
        <div className="ps-actions">
          <button className="btn" disabled={picked.chosen.length === 0} title={picked.chosen.length === 0 ? 'Listeden ürün seçin' : undefined} onClick={() => setLabeling(picked.chosen)}>
            <Icon name="printer" size={18} /> Etiket bas{picked.chosen.length > 0 ? ` (${picked.chosen.length})` : ''}
          </button>
          {pick.canEdit && (
            <>
              <a className="btn" href="#/urunler/eslestirme">
                <Icon name="link" size={18} /> <span className="long">Pazaryeri eşleştirme</span>
                <span className="short">Eşleştirme</span>
              </a>
              <a className="btn" href="#/urunler/ice-aktar">
                <Icon name="upload" size={18} /> <span className="long">Excel / CSV ile yükle</span>
                <span className="short">Excel ile yükle</span>
              </a>
              <button className="btn primary" onClick={() => navigate('/urunler/yeni')}>
                <Icon name="plus" /> Yeni ürün
              </button>
            </>
          )}
        </div>
      </div>
      {/* a phone: one row that slides sideways, the status at its end */}
      <div className="ps-filters" role="group" aria-label="Süz">
        {STOCK_FILTERS.map((f) => (
          <button key={f.key} className={`chip select${filter === f.key ? ' on' : ''}`} onClick={() => setFilter(f.key)} aria-pressed={filter === f.key}>
            {f.label}
            {counted && <span className="n">{fmt.n(listed.filter((p) => inFilter(p, f.key)).length)}</span>}
          </button>
        ))}
        <select className="select ps-status" value={status} onChange={(e) => setStatus(e.target.value as ProductStatus | '')} aria-label="Ürün durumu">
          {STATUSES.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
      </div>
      {loaded && rows.length > 0 && (
        <span className="muted">
          {rows.length} ürün · {total((p) => p.available)} kullanılabilir · {total((p) => p.physical)} depoda · lotlar ve SKT için ürüne dokunun
        </span>
      )}
      <section className="card">
        {list.error && <ErrorBanner message={list.error} onRetry={() => void list.reload()} />}
        {stock.error && <ErrorBanner message={`Stok yüklenemedi: ${stock.error}`} onRetry={() => void stock.reload()} />}
        {!loaded ? (
          <Spinner />
        ) : all.length === 0 ? (
          <EmptyCatalog pick={pick} term={term} fresh={!term} />
        ) : rows.length === 0 ? (
          <Empty title={term ? 'Aramaya uyan ürün bu filtrede yok' : 'Bu filtrede ürün yok'} />
        ) : (
          <div className="table-wrap">
            <table className="t stack-sm ps-list">
              <thead>
                <tr>
                  <th className="ps-pick">
                    <label>
                      <input type="checkbox" aria-label="Tümünü seç" checked={picked.all} onChange={picked.toggleAll} />
                    </label>
                  </th>
                  {sort.th('product')}
                  {sort.th('available', 'num')}
                  {sort.th('reserved', 'num')}
                  {sort.th('inbound', 'num')}
                  {blocked && sort.th('blocked', 'num')}
                  {sort.th('expiry')}
                  {sort.th('listings', 'num hide-sm')}
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr
                    key={p.id}
                    className="clickable"
                    onClick={() => {
                      openedFrom = { userId, filter, status, search };
                      navigate(`/urunler/${p.id}`);
                    }}
                  >
                    <td className="ps-pick" onClick={(e) => e.stopPropagation()}>
                      <label>
                        <input type="checkbox" aria-label={`${p.sku} seç`} checked={picked.has(p.id)} onChange={() => picked.toggle(p.id)} />
                      </label>
                    </td>
                    <td className="ps-product">
                      <div className="product compact">
                        <ProductThumb name={p.name} imageUrl={p.imageUrl} small />
                        <div style={{ minWidth: 0 }}>
                          <div className="name">{p.name}</div>
                          {/* the barcode for the desk; on a phone only its absence */}
                          <div className="muted mono ps-tags">
                            {p.sku}
                            {p.primaryBarcode ? <span className="ps-code">· {p.primaryBarcode}</span> : <span className="chip warn">Barkod yok</span>}
                            {p.desi === null && <span className="chip warn">Ölçü yok</span>}
                            {p.status !== 'active' && <span className="chip">{STATUS_LABELS[p.status]}</span>}
                            {p.trackLot && <span className="chip">Lot</span>}
                            {p.trackExpiry && <span className="chip">SKT</span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="num" data-label="Kullanılabilir">
                      {p.available > 0 ? <b>{fmt.n(p.available)}</b> : <span className="chip bad">Tükendi</span>}
                    </td>
                    <td className={`num${p.reserved ? '' : ' zero'}`} data-label="Rezerve">
                      {p.reserved ? fmt.n(p.reserved) : '—'}
                    </td>
                    <td className={`num${p.inbound ? '' : ' zero'}`} data-label="Kabulde / yolda">
                      {p.inbound ? fmt.n(p.inbound) : '—'}
                    </td>
                    {blocked && (
                      <td className={`num${p.blocked ? '' : ' zero'}`} data-label="Bloke">
                        {p.blocked ? <span className="chip warn">{fmt.n(p.blocked)}</span> : '—'}
                      </td>
                    )}
                    <td className={p.nextExpiry || p.expiredQty > 0 ? 'ps-expiry' : 'zero'} data-label="SKT">
                      {p.expiredQty > 0 && <span className="chip bad">{fmt.n(p.expiredQty)} adet SKT geçti</span>}
                      {p.nextExpiry ? <ExpiryChip date={p.nextExpiry} minShelfLifeDays={p.minShelfLifeDays} /> : p.trackExpiry ? <span className="muted">—</span> : <span className="muted">takip yok</span>}
                    </td>
                    <td className="num hide-sm">{p.listings > 0 ? <span className="chip ok">{p.listings}</span> : <span className="muted">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {labeling && <ProductLabelDialog products={labelItems(labeling)} onClose={() => setLabeling(null)} />}
    </>
  );
}
